import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { QueryError } from 'src/queries/network.utils'
import { GoogleEmailIntegrationResponse } from 'src/queries/organizations/emailIntegrations/emailIntegrations.models'
import {
  connectGoogleEmailIntegration,
  disconnectGoogleEmailIntegration,
  getGoogleEmailIntegration,
} from 'src/queries/organizations/emailIntegrations/emailIntegrations.network'
import { CustomMutationOptions } from 'src/queries/queries.utils'

export const emailIntegrationQueryKeys = {
  google: (orgId: string | undefined) =>
    ['emailIntegrations', 'google', orgId] as const,
}

const getGoogleEmailIntegrationForOrganization = (
  orgId: string | undefined,
) => {
  if (!orgId) throw new Error('Email integration organization id is required')
  return getGoogleEmailIntegration(orgId)
}

export const emailIntegrationQueries = {
  google: (orgId: string | undefined) =>
    queryOptions({
      queryKey: emailIntegrationQueryKeys.google(orgId),
      queryFn: () => getGoogleEmailIntegrationForOrganization(orgId),
      enabled: Boolean(orgId),
      staleTime: 5 * 60 * 1000,
    }),
}

export interface EmailIntegrationMutationParams {
  orgId: string
}

const requireOrganizationId = (orgId: string) => {
  if (!orgId) throw new Error('Email integration organization id is required')
  return orgId
}

const emailIntegrationMutations = {
  connectGoogle: () =>
    mutationOptions<
      GoogleEmailIntegrationResponse,
      QueryError,
      EmailIntegrationMutationParams
    >({
      mutationFn: ({ orgId }) =>
        connectGoogleEmailIntegration(requireOrganizationId(orgId)),
    }),
  disconnectGoogle: () =>
    mutationOptions<
      GoogleEmailIntegrationResponse,
      QueryError,
      EmailIntegrationMutationParams
    >({
      mutationFn: ({ orgId }) =>
        disconnectGoogleEmailIntegration(requireOrganizationId(orgId)),
    }),
}

type EmailIntegrationMutationOptions = CustomMutationOptions<
  GoogleEmailIntegrationResponse,
  QueryError,
  EmailIntegrationMutationParams
>

export function useConnectGoogleEmailIntegration(
  options?: EmailIntegrationMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onError, onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...emailIntegrationMutations.connectGoogle(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: emailIntegrationQueryKeys.google(variables.orgId),
        exact: true,
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      if (error.errorCode === 'EMAIL_INTEGRATION_DELEGATION_NOT_READY') {
        queryClient.invalidateQueries({
          queryKey: emailIntegrationQueryKeys.google(variables.orgId),
          exact: true,
        })
      }
      onError?.(error, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}

export function useDisconnectGoogleEmailIntegration(
  options?: EmailIntegrationMutationOptions,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}

  return useMutation({
    ...emailIntegrationMutations.disconnectGoogle(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: emailIntegrationQueryKeys.google(variables.orgId),
        exact: true,
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
